/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useDispatch } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { useAuth } from '../modules/auth'
import { updateCardById } from '../services/cards'
import { upsertCurrentUserCard } from '../store/slice/card-slice'

/**
 * Save just the description field of a card directly, bypassing any form
 * UI. Use this when the editor makes a change that must persist even if
 * the user never clicks the parent form's Save button — e.g. after
 * linking a checklist item to a newly-created task so the link survives
 * a modal close.
 *
 * Builds the payload from the provided `card` so all required fields are
 * present, overrides only `description`, and merges the backend response
 * into the redux `allCurrentUserCards` list so downstream consumers stay
 * in sync without a refetch.
 */
export function useSaveCardDescription() {
  const dispatch: Dispatch<any> = useDispatch()
  const { currentUser } = useAuth()
  const { id: userId, fullName } = currentUser?.data?.user || {}

  return async (card: any, description: string | null): Promise<any> => {
    if (!card?.id || !card?.boardId || !userId) return null
    const payload: any = {
      created_by: card.created_by ?? fullName,
      last_modified_by: fullName,
      board_name: card.board_name.trim(),
      card_name: card.card_name,
      description,
      due_date: card.due_date ?? null,
      due_time: card.due_time ?? null,
      position: card.position,
      home_position: card.home_position ?? card.position,
      priority: card.priority ?? null,
      rag: card.rag ?? null,
      status: card.status,
      version: card.version ?? 1,
      boardId: card.boardId,
      board_column_id: card.board_column_id,
      reporter: card.reporter ?? fullName,
    }
    try {
      const res: any = await updateCardById(payload, card.id, userId, card.boardId)
      const updatedCard = res?.data?.data?.card ?? res?.data?.data ?? null
      if (updatedCard?.id) dispatch(upsertCurrentUserCard(updatedCard))
      return updatedCard
    } catch {
      return null
    }
  }
}
